import React, { useId, useRef } from "react";
import { AttachmentItem } from "./types";
import { UseAttachmentsFieldResult } from "./useAttachmentsField";

export interface AttachmentsFieldProps {
  attachmentsField: UseAttachmentsFieldResult;
  label?: string;
  helperText?: string;
  accept?: string;
  disabled?: boolean;
  fieldId?: string;
}

const DEFAULT_ACCEPT =
  ".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt,.xls,.xlsx";

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const getFileIcon = (fileName: string | null | undefined): string => {
  if (!fileName) {
    return "fas fa-file text-muted";
  }
  const extension = fileName.split(".").pop()?.toLowerCase();
  switch (extension) {
    case "pdf":
      return "fas fa-file-pdf text-danger";
    case "doc":
    case "docx":
      return "fas fa-file-word text-primary";
    case "xls":
    case "xlsx":
      return "fas fa-file-excel text-success";
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return "fas fa-file-image text-info";
    case "txt":
      return "fas fa-file-alt text-secondary";
    default:
      return "fas fa-file text-muted";
  }
};

const getExistingAttachmentName = (
  attachment: AttachmentItem,
  index: number
): string =>
  attachment.file_name || attachment.title || `Attachment ${index + 1}`;

export const AttachmentsField: React.FC<AttachmentsFieldProps> = ({
  attachmentsField,
  label = "Attachments",
  helperText,
  accept = DEFAULT_ACCEPT,
  disabled = false,
  fieldId,
}) => {
  const {
    files,
    existingAttachments,
    attachmentsToDelete,
    error,
    maxFiles,
    maxFileSizeMB,
    addFiles,
    removeFile,
    markForDeletion,
    unmarkForDeletion,
  } = attachmentsField;

  const generatedId = useId();
  const inputId = fieldId || `attachments-file-input-${generatedId}`;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const totalCurrentFiles = files.length + existingAttachments.length;
  const isAtLimit = totalCurrentFiles >= maxFiles;
  const hasAnyAttachments = files.length > 0 || existingAttachments.length > 0;

  const counterClass = isAtLimit
    ? "text-danger fw-bold"
    : totalCurrentFiles >= maxFiles - 1
    ? "text-warning fw-bold"
    : "text-muted";

  const resolvedHelperText =
    helperText ??
    `PDF, DOC, DOCX, JPG, PNG, GIF, TXT, XLS, XLSX (Max ${maxFileSizeMB}MB per file, up to ${maxFiles} files)`;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files;
    if (selected && selected.length > 0) {
      addFiles(selected);
    }
    event.target.value = "";
  };

  return (
    <div className="mb-3">
      <label className="col-form-label">{label}</label>
      <div className="mb-2 d-flex align-items-center gap-2">
        <button
          type="button"
          className="btn btn-light btn-sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isAtLimit}
          style={{ minWidth: "120px" }}
          title={
            isAtLimit
              ? `Maximum file limit reached (${maxFiles} files)`
              : "Add attachments"
          }
        >
          <i className="fas fa-paperclip me-1"></i>
          {label}
        </button>
        <small className="text-muted">{resolvedHelperText}</small>
        <div className="ms-auto">
          <small className={counterClass}>
            {totalCurrentFiles}/{maxFiles} files
          </small>
        </div>
        <input
          id={inputId}
          ref={fileInputRef}
          type="file"
          className="d-none"
          onChange={handleInputChange}
          multiple
          accept={accept}
        />
      </div>

      {error && (
        <div className="alert alert-danger py-2">
          <small>{error}</small>
        </div>
      )}

      {hasAnyAttachments && (
        <div className="mt-3">
          <h6 className="mb-2">Selected Files:</h6>
          <div className="list-group">
            {files.map((file, index) => (
              <div
                key={`new-${index}`}
                className="list-group-item d-flex justify-content-between align-items-center py-2"
              >
                <div className="d-flex align-items-center">
                  <i className={`${getFileIcon(file.name)} me-2`}></i>
                  <div>
                    <div className="fw-medium">{file.name}</div>
                    <small className="text-muted">
                      {formatFileSize(file.size)}
                    </small>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => removeFile(index)}
                  disabled={disabled}
                  title="Remove file"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}

            {existingAttachments.map((attachment, index) => {
              const attachmentId = attachment.attachment_id as number;
              const isMarked = attachmentsToDelete.includes(attachmentId);

              return (
                <div
                  key={`existing-${attachmentId ?? index}`}
                  className={`list-group-item d-flex justify-content-between align-items-center py-2 ${
                    isMarked ? "bg-light" : ""
                  }`}
                >
                  <div className="d-flex align-items-center">
                    <i
                      className={`${getFileIcon(
                        getExistingAttachmentName(attachment, index)
                      )} me-2`}
                    ></i>
                    <div>
                      <div
                        className={`fw-medium ${
                          isMarked ? "text-decoration-line-through text-muted" : ""
                        }`}
                      >
                        {attachment.attachment_url ? (
                          <a
                            href={attachment.attachment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-decoration-none"
                          >
                            {getExistingAttachmentName(attachment, index)}
                          </a>
                        ) : (
                          getExistingAttachmentName(attachment, index)
                        )}
                      </div>
                      <small className="text-muted">
                        {attachment.size_in_kb
                          ? `${attachment.size_in_kb} KB`
                          : "Unknown size"}{" "}
                        •
                        {attachment.created_datetime
                          ? ` Created: ${new Date(
                              attachment.created_datetime
                            ).toLocaleDateString()}`
                          : ""}
                        {isMarked ? " • Marked for deletion" : ""}
                      </small>
                    </div>
                  </div>
                  {isMarked ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => unmarkForDeletion(attachmentId)}
                      disabled={disabled}
                      title="Undo removal"
                    >
                      <i className="fas fa-rotate-left"></i>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => markForDeletion(attachmentId)}
                      disabled={disabled}
                      title="Mark for deletion"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttachmentsField;
